import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  createFloor,
  CreateFloorParams,
  deleteFloor,
  readFloors,
  updateFloor,
  UpdateFloorParams,
} from 'src/api/plan';
import { Floor } from 'src/api/types';
import { defaultServerError } from 'src/utils/consts';

import { messagesActions } from '../messages/slice';
import { sidesheetActions } from '../sidesheet/slice';
import { StoreState, ThunkConfig } from '../types';

import { getFloorsState } from './selectors';

export interface FetchFloorsParams {
  cached: boolean;
  buildingId: number;
}

export const fetchFloors = createAsyncThunk<
  Floor[],
  FetchFloorsParams,
  ThunkConfig<string>
>(
  'floors/fetchFloors',
  async ({ cached, buildingId }, { getState, dispatch, rejectWithValue }) => {
    try {
      if (cached) {
        const state = getState() as StoreState;

        const { floorsByBuilding, floorsById } = getFloorsState(state);
        const cachedFloorIds = floorsByBuilding.get(buildingId);
        if (cachedFloorIds?.size) {
          const cachedFloors: Floor[] = [];

          for (const floorId of cachedFloorIds) {
            const floor = floorsById.get(floorId);

            if (floor) cachedFloors.push(floor);
          }

          if (cachedFloors.length) return cachedFloors;
        }
      }

      return readFloors(buildingId);
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
  {
    condition: ({ buildingId }, { getState }) => {
      const state = getState() as StoreState;
      const { currentRequests } = getFloorsState(state);

      return !currentRequests.has(buildingId);
    },
  },
);

export const removeFloor = createAsyncThunk<void, number, ThunkConfig<string>>(
  'floors/removeFloor',
  async (id, { getState, dispatch, rejectWithValue }) => {
    try {
      const { floorsById } = getFloorsState(getState() as StoreState);
      const floor = floorsById.get(id);
      if (!floor) return;

      await deleteFloor(id);
      dispatch(
        messagesActions.show({
          message: `Этаж ${floor.title} успешно удален!`,
        }),
      );
      dispatch(sidesheetActions.close());
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);

export const addFloor = createAsyncThunk<
  Floor,
  CreateFloorParams,
  ThunkConfig<string>
>('floors/addFloor', async (params, { dispatch, rejectWithValue }) => {
  try {
    const floor = await createFloor(params);
    dispatch(
      messagesActions.show({
        message: `Этаж ${floor.title} успешно добавлен!`,
      }),
    );
    dispatch(sidesheetActions.close());

    return floor;
  } catch (e) {
    dispatch(messagesActions.show({ message: defaultServerError }));
    return rejectWithValue((e as AxiosError).message);
  }
});

export const changeFloor = createAsyncThunk<
  Floor,
  UpdateFloorParams,
  ThunkConfig<string>
>('floors/changeTown', async (params, { dispatch, rejectWithValue }) => {
  try {
    const floor = await updateFloor(params);
    dispatch(
      messagesActions.show({
        message: `Этаж ${floor.title} успешно обновлен!`,
      }),
    );
    dispatch(sidesheetActions.close());

    return floor;
  } catch (e) {
    dispatch(messagesActions.show({ message: defaultServerError }));
    return rejectWithValue((e as AxiosError).message);
  }
});
